import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue
} from '@gettactic/components/src/@v2/components/ui/select';
import { SelectItem } from '@gettactic/components/src/@v2/components/ui/select';
import { FormControlWrapper } from '@gettactic/components/src/form/FormControlWrapper';
import { FormControlOutline } from '@gettactic/components/src/form/FormControlWrapper/FormControlOutline';
import { cn } from '@gettactic/helpers';
import { UserAvatar } from '@v2/components/display/UserAvatar/UserAvatar';
import React, { useMemo } from 'react';
import { useController } from 'react-hook-form';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
  profile_photo_url: string;
  default_organization_id: string;
  roles: string[];
}

interface OwnerSelectProps {
  variant?: 'modal' | 'table';
  value?: string;
  onValueChange?: (value: string) => void;
  assignedUsers: User[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  
  form?: any;
  name?: string;
}

export const OwnerSelect = React.forwardRef<
  HTMLButtonElement,
  OwnerSelectProps
>(
  (
    {
      variant = 'modal',
      value,
      onValueChange,
      assignedUsers,
      placeholder = 'Select owner',
      className,
      disabled = false,
      form,
      name
    },
    ref
  ) => {
    
    const formController =
      form && name
        ? useController({
            name,
            control: form.control
          })
        : null;

    const currentValue = formController ? formController.field.value : value;

    
    const handleValueChange = useMemo(() => {
      return formController ? formController.field.onChange : onValueChange;
    }, [formController, onValueChange]);

    const error = formController?.fieldState.error;

    if (variant === 'modal') {
      return (
        <FormControlWrapper
          error={error?.message}
          isInvalid={!!error}
          enableSlotsInputFocus={true}
        >
          <Select value={currentValue} onValueChange={handleValueChange}>
            <SelectTrigger
              ref={ref}
              className={cn(
                'flex h-8 min-h-8 min-w-0 max-w-full truncate border-0 bg-transparent shadow-none focus:ring-0',
                className
              )}
              disabled={disabled}
            >
              <div className="flex min-w-0 flex-1 items-center justify-start">
                <SelectValue
                  placeholder={placeholder}
                  className="max-w-full flex-1 truncate text-left"
                />
              </div>
            </SelectTrigger>
            <SelectContent
              className="z-dropdown"
              position="popper"
              sideOffset={4}
            >
              {assignedUsers.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormControlOutline />
        </FormControlWrapper>
      );
    }
    const selectedUser = assignedUsers.find((u) => u.id === currentValue);
    
    return (
      <Select value={currentValue} onValueChange={handleValueChange}>
        <SelectTrigger
          ref={ref}
          className={cn(
            'flex h-auto min-h-0 justify-start space-x-1 rounded border-0 bg-transparent p-0 shadow-none hover:bg-gray-50 focus:ring-0',
            className
          )}
          disabled={disabled}
        >
          <div className="flex min-w-0 items-center">
            {currentValue ? (
              <>
                <UserAvatar
                  user={{
                    name: selectedUser?.name || '',
                    email: selectedUser?.email || '',
                    profile_photo_url: selectedUser?.profile_photo_url || ''
                  }}
                  size="xs"
                  className="mr-2 flex-shrink-0"
                />
                <span className="truncate font-inter text-gray-900 text-xs">
                  {selectedUser?.name}
                </span>
              </>
            ) : (
              <span className="truncate font-inter text-gray-500 text-xs">
                {placeholder}
              </span>
            )}
          </div>
        </SelectTrigger>
        <SelectContent className="z-dropdown" position="popper" sideOffset={4}>
          {assignedUsers.map((user) => (
            <SelectItem key={user.id} value={user.id}>
              <div className="flex items-center">
                <UserAvatar
                  user={{
                    name: user.name,
                    email: user.email,
                    profile_photo_url: user.profile_photo_url
                  }}
                  size="xs"
                  className="mr-2"
                />
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{user.name}</span>
                  <span className="text-gray-500 text-xs">{user.email}</span>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
);

OwnerSelect.displayName = 'OwnerSelect';
